import { Flex, FlexProps, Icon, Text, Tooltip } from '@chakra-ui/react'
import React, { Key, useMemo } from 'react'
import { BetaBadge } from '~/components/shared/BetaBadge'
import { NewBadge } from '~/components/shared/NewBadge'
import { NotificationBadge } from '~/components/shared/NotificationBadge'
import {
  SiteNavigationTabSizes,
  useSiteNavigationTabStyle,
} from '~/pages/Home/SiteNavigation/componentStyles/SiteNavigationTab.style'

const MAX_NOTIFICATION_COUNT = 999
const getNotificationCountString = (count?: number) => {
  if (count === undefined || count <= 0) return undefined

  return count > MAX_NOTIFICATION_COUNT ? `${MAX_NOTIFICATION_COUNT}+` : count
}

interface SiteNavigationTabProps extends Omit<FlexProps, 'id' | 'onClick'> {
  id?: Key
  title: string
  isCollapsed?: boolean
  icon?: SVGComponent
  isSelected?: boolean
  isBeta?: boolean
  isNew?: boolean
  notificationCount?: number
  postfixContent?: React.ReactNode
  onClick?: (id?: Key) => void
}

export const SiteNavigationTab = (props: SiteNavigationTabProps) => {
  const {
    id,
    title,
    isCollapsed,
    icon,
    isSelected,
    isBeta,
    isNew,
    notificationCount,
    postfixContent,
    onClick,
    ...rest
  } = props

  const getTabSize = () => {
    if (isCollapsed && (isNew || isBeta)) {
      return SiteNavigationTabSizes.CollapsedBadged
    }
    if (isCollapsed) {
      return SiteNavigationTabSizes.Collapsed
    }

    return SiteNavigationTabSizes.Normal
  }

  const styles = useSiteNavigationTabStyle({
    size: getTabSize(),
  })

  const handleClick = () => {
    onClick?.(id)
  }

  const notificationCountString = getNotificationCountString(notificationCount)
  const tabContent = useMemo(() => {
    if (!isCollapsed) {
      return (
        <Flex key={id} sx={styles.container}>
          <Flex
            sx={styles.selectionContainer}
            {...rest}
            aria-selected={isSelected}
            onClick={handleClick}
          >
            <Flex sx={styles.contentWrapper}>
              <Flex sx={styles.iconWrapper}>
                {icon && (
                  <Icon as={icon} aria-selected={isSelected} sx={styles.icon} />
                )}
                <Text
                  sx={styles.titleText}
                  noOfLines={1}
                  aria-selected={isSelected}
                >
                  {title}
                </Text>
              </Flex>

              {notificationCountString && (
                <NotificationBadge
                  count={notificationCountString}
                  sx={styles.notificationBadge}
                />
              )}
              {isBeta && <BetaBadge sx={styles.statusBadge} />}
              {isNew && <NewBadge sx={styles.statusBadge} />}
              {postfixContent}
            </Flex>
          </Flex>
        </Flex>
      )
    }

    return (
      <Flex key={id} sx={styles.container}>
        <Tooltip hasArrow label={title} placement="right">
          <Flex
            sx={styles.selectionContainer}
            aria-selected={isSelected}
            onClick={handleClick}
          >
            {icon && (
              <Icon as={icon} aria-selected={isSelected} sx={styles.icon} />
            )}
            {notificationCountString && (
              <NotificationBadge
                count={notificationCountString}
                sx={styles.notificationBadge}
              />
            )}
            {isBeta && <BetaBadge sx={styles.statusBadge} />}
            {isNew && <NewBadge sx={styles.statusBadge} />}
            {postfixContent}
          </Flex>
        </Tooltip>
      </Flex>
    )
  }, [
    title,
    isCollapsed,
    icon,
    isSelected,
    isBeta,
    isNew,
    notificationCount,
    postfixContent,
  ])

  return <>{tabContent}</>
}
